import React, { Fragment, memo, useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { T } from '@colonist/ui';
import styles from './Table.module.scss';
import clsx from 'clsx';
function overflowClass(overflow) {
    switch(overflow){
        case 'nowrap':
            return styles.nowrap;
        case 'truncate':
            return styles.truncate;
        case 'wrap':
            return undefined;
    }
}
function toColumnDefs(columns) {
    return columns.map((col)=>{
        const cellFn = col.cell;
        var _col_overflow;
        const def = {
            id: col.id,
            accessorKey: col.id,
            header: col.header,
            meta: {
                overflow: (_col_overflow = col.overflow) !== null && _col_overflow !== void 0 ? _col_overflow : 'truncate',
                ...col.align != undefined && {
                    align: col.align
                },
                ...col.width != undefined && {
                    width: col.width
                },
                ...col.className != undefined && {
                    className: col.className
                }
            }
        };
        if (cellFn) {
            def.cell = (info)=>cellFn({
                    value: info.getValue(),
                    row: info.row.original
                });
        }
        return def;
    });
}
export const FillerRow = ({ colSpan })=>/*#__PURE__*/ React.createElement("tr", {
        className: styles.fillerRow,
        "data-testid": "table-filler-row"
    }, /*#__PURE__*/ React.createElement("td", {
        colSpan: colSpan,
        className: styles.fillerCell
    }));
function TableComponent(props) {
    const { data, columns, onRowClick, getRowProps, getCellProps, containerRef, headRef, emptyText, showEmptyText, backgroundStyle = 'filled', fillRemainingHeight = 'afterRows', density = 'comfortable', className, testId } = props;
    const hasNoData = data.length == 0;
    const shouldShowEmptyText = (showEmptyText || hasNoData) && emptyText != undefined;
    const shouldShowFiller = fillRemainingHeight != 'none' && !shouldShowEmptyText;
    const fillerBeforeRows = shouldShowFiller && fillRemainingHeight == 'beforeRows';
    const fillerAfterRows = shouldShowFiller && fillRemainingHeight == 'afterRows';
    const columnDefs = useMemo(()=>toColumnDefs(columns), [
        columns
    ]);
    const table = useReactTable({
        data: data,
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel()
    });
    return /*#__PURE__*/ React.createElement("div", {
        ref: containerRef,
        className: clsx(styles.container, className),
        "data-testid": testId
    }, /*#__PURE__*/ React.createElement("table", {
        className: clsx(styles.table, styles[backgroundStyle], density === 'compact' ? styles.densityCompact : styles.densityComfortable)
    }, /*#__PURE__*/ React.createElement("thead", {
        ref: headRef,
        className: styles.tableHeader
    }, table.getHeaderGroups().map((headerGroup)=>/*#__PURE__*/ React.createElement("tr", {
            key: headerGroup.id
        }, headerGroup.headers.map((header)=>{
            var _header_column_columnDef_meta, _header_column_columnDef_meta1, _header_column_columnDef_meta2, _header_column_columnDef_meta3;
            var _header_column_columnDef_meta_overflow, _header_column_columnDef_meta_align;
            return /*#__PURE__*/ React.createElement("th", {
                key: header.id,
                colSpan: header.colSpan,
                className: clsx(styles.tableHeaderCell, overflowClass((_header_column_columnDef_meta_overflow = (_header_column_columnDef_meta = header.column.columnDef.meta) === null || _header_column_columnDef_meta === void 0 ? void 0 : _header_column_columnDef_meta.overflow) !== null && _header_column_columnDef_meta_overflow !== void 0 ? _header_column_columnDef_meta_overflow : 'truncate'), (_header_column_columnDef_meta1 = header.column.columnDef.meta) === null || _header_column_columnDef_meta1 === void 0 ? void 0 : _header_column_columnDef_meta1.className),
                style: {
                    textAlign: (_header_column_columnDef_meta_align = (_header_column_columnDef_meta2 = header.column.columnDef.meta) === null || _header_column_columnDef_meta2 === void 0 ? void 0 : _header_column_columnDef_meta2.align) !== null && _header_column_columnDef_meta_align !== void 0 ? _header_column_columnDef_meta_align : 'left',
                    width: (_header_column_columnDef_meta3 = header.column.columnDef.meta) === null || _header_column_columnDef_meta3 === void 0 ? void 0 : _header_column_columnDef_meta3.width
                }
            }, flexRender(header.column.columnDef.header, header.getContext()));
        })))), /*#__PURE__*/ React.createElement("tbody", null, shouldShowEmptyText && /*#__PURE__*/ React.createElement("tr", {
        className: styles.emptyRow
    }, /*#__PURE__*/ React.createElement("td", {
        colSpan: columns.length,
        className: styles.emptyContainer
    }, /*#__PURE__*/ React.createElement(T, {
        id: emptyText
    }))), fillerBeforeRows && /*#__PURE__*/ React.createElement(FillerRow, {
        colSpan: columns.length
    }), table.getRowModel().rows.map((row)=>{
        const extraRowProps = getRowProps === null || getRowProps === void 0 ? void 0 : getRowProps(row.original, row.index);
        return /*#__PURE__*/ React.createElement(Fragment, {
            key: row.id
        }, extraRowProps === null || extraRowProps === void 0 ? void 0 : extraRowProps.prefixComponent, /*#__PURE__*/ React.createElement("tr", {
            className: clsx(styles.tableRow, {
                [styles.clickable]: onRowClick
            }, extraRowProps === null || extraRowProps === void 0 ? void 0 : extraRowProps.className),
            style: extraRowProps === null || extraRowProps === void 0 ? void 0 : extraRowProps.style,
            onClick: onRowClick ? ()=>onRowClick(row.original) : undefined
        }, row.getVisibleCells().map((cell)=>{
            var _cell_column_columnDef_meta, _cell_column_columnDef_meta1, _cell_column_columnDef_meta2;
            const extraCellProps = getCellProps === null || getCellProps === void 0 ? void 0 : getCellProps(row.original, cell.column.id, row.index);
            var _cell_column_columnDef_meta_overflow, _cell_column_columnDef_meta_align;
            return /*#__PURE__*/ React.createElement("td", {
                key: cell.id,
                colSpan: extraCellProps === null || extraCellProps === void 0 ? void 0 : extraCellProps.colSpan,
                className: clsx(styles.tableCell, overflowClass((_cell_column_columnDef_meta_overflow = (_cell_column_columnDef_meta = cell.column.columnDef.meta) === null || _cell_column_columnDef_meta === void 0 ? void 0 : _cell_column_columnDef_meta.overflow) !== null && _cell_column_columnDef_meta_overflow !== void 0 ? _cell_column_columnDef_meta_overflow : 'truncate'), (_cell_column_columnDef_meta1 = cell.column.columnDef.meta) === null || _cell_column_columnDef_meta1 === void 0 ? void 0 : _cell_column_columnDef_meta1.className, extraCellProps === null || extraCellProps === void 0 ? void 0 : extraCellProps.className),
                style: {
                    textAlign: (_cell_column_columnDef_meta_align = (_cell_column_columnDef_meta2 = cell.column.columnDef.meta) === null || _cell_column_columnDef_meta2 === void 0 ? void 0 : _cell_column_columnDef_meta2.align) !== null && _cell_column_columnDef_meta_align !== void 0 ? _cell_column_columnDef_meta_align : 'left'
                }
            }, flexRender(cell.column.columnDef.cell, cell.getContext()));
        })), extraRowProps === null || extraRowProps === void 0 ? void 0 : extraRowProps.suffixComponent);
    }), fillerAfterRows && /*#__PURE__*/ React.createElement(FillerRow, {
        colSpan: columns.length
    }))));
}
export const Table = /*#__PURE__*/ memo(TableComponent);
